import React, { useState, useEffect } from 'react';
import Footer from '../components/Footer';
import './TruthTellersRequest.css';

/**
 * Truth Tellers Request Page
 * Alternative landing page for requesting diagnostic assessments
 * Features a more marketing-focused design with the "Truth Tellers" branding
 */
function TruthTellersRequest() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    gdprConsent: false,
    diagnosticType: ''
  });

  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDiagnosticSelect = (value) => {
    setFormData(prev => ({ ...prev, diagnosticType: value }));
    if (errors.diagnosticType) {
      setErrors(prev => ({ ...prev, diagnosticType: '' }));
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && !emailRegex.test(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.gdprConsent) {
      newErrors.gdprConsent = 'You must consent to GDPR terms';
    }
    if (!formData.diagnosticType) {
      newErrors.diagnosticType = 'Please select a diagnostic type';
    }

    if (Object.keys(newErrors).length > 0) {
      newErrors.general = 'Please fill in all required fields';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitMessage('');

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('/api/diagnostic-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          requestingPageId: 2  // Truth Tellers request form
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage(data.message || 'Diagnostic request sent! Check your email for the link.');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          gdprConsent: false,
          diagnosticType: ''
        });
      } else {
        setErrors({ general: data.error || 'Failed to submit request. Please try again.' });
      }
    } catch (error) {
      setErrors({ general: 'Network error. Please check your connection and try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const diagnosticOptions = [
    {
      id: 'system_burden',
      title: 'THE LEAK (Functional/System)',
      description: 'Where is transformation value leaking? Delayed decisions, misalignment, unresolved conflicts.',
      cta: 'In 5 minutes, reveal the structural gaps draining your system.',
      icon: '/images/truth-tellers/leak.svg'
    },
    {
      id: 'leadership_burden',
      title: 'CAPABILITY (Leadership/Personal)',
      description: 'Can you actually do what\'s being asked? Managing complexity, distinguish uncertainty, maintaining composure.',
      cta: 'In 5 minutes measure your capacity for meeting your current demands.',
      icon: '/images/truth-tellers/capability.svg'
    },
    {
      id: 'psychological_flexibility',
      title: 'WELLBEING (Psychological/Social)',
      description: 'What\'s the personal toll? Self-doubt, reputation anxiety, managing others\' perception of you.',
      cta: 'In 5 minutes quantify your psychological load of performing under scrutiny.',
      icon: '/images/truth-tellers/wellbeing.svg'
    }
  ];

  return (
    <div className="truth-tellers-page">
      <main className="truth-tellers-main">
        {/* ROW 1: Hero Image + Title */}
        <div className="tt-row">
          <div className="tt-row-cell">
            <img
              src="/images/truth-tellers/hero.png"
              alt="Truth Teller - Superhero speaking truth through megaphone"
              className="tt-hero-image"
            />
          </div>
          <div className="tt-row-cell tt-hero-title">
            <h1>Truth Tellers:<br/>Diagnosing Hidden<br/>Work</h1>
            <p className="tt-hero-subtitle">Calculate what you're carrying<br/>and what it's costing</p>
          </div>
        </div>

        {/* ROW 2: Three Truth Tellers + What You'll Gain */}
        <div className="tt-row">
          <div className="tt-row-cell">
            <h2 className="tt-section-header">Three Truth Tellers. 15 Mins</h2>
            <p className="tt-intro-text">
              <strong>Three diagnostic lenses revealing:</strong>
            </p>
            <ul className="tt-check-list">
              <li>Where transformation value leaks</li>
              <li>Where capability gaps exist</li>
              <li>What psychological burden you're carrying</li>
            </ul>
            <p className="tt-instruction-text">
              Take all three to see the complete picture - or start with the one that speaks to your current biggest pain point.
            </p>
          </div>
          <div className="tt-row-cell">
            <h2 className="tt-section-header">What You'll Gain</h2>
            <ul className="tt-check-list">
              <li>Quantified hidden work load - 20-40% of effort</li>
              <li>System-level leaks identified</li>
              <li>Personal capability gaps measured</li>
              <li>Psychological burden quantified</li>
              <li>Recoverable value calculated</li>
            </ul>
          </div>
        </div>

        {/* ROW 3: THE LEAK + CAPABILITY */}
        <div className="tt-row">
          {diagnosticOptions.slice(0, 2).map(option => (
            <label
              key={option.id}
              className={`tt-diagnostic-card ${formData.diagnosticType === option.id ? 'selected' : ''}`}
              onClick={() => handleDiagnosticSelect(option.id)}
            >
              <input
                type="radio"
                name="diagnosticType"
                value={option.id}
                checked={formData.diagnosticType === option.id}
                onChange={() => handleDiagnosticSelect(option.id)}
                className="tt-diagnostic-radio"
              />
              <span className="tt-checkbox-indicator"></span>
              <div className="tt-diagnostic-icon">
                <img src={option.icon} alt={`${option.title} icon`} />
              </div>
              <div className="tt-diagnostic-content">
                <h3 className="tt-diagnostic-title">{option.title}</h3>
                <p className="tt-diagnostic-description">
                  {option.description}
                  <br/><br/>
                  <strong>{option.cta}</strong>
                </p>
                <div className="tt-selected-indicator">
                  <span className="tt-selected-check">✓</span> Diagnostic selected
                </div>
              </div>
            </label>
          ))}
        </div>

        {/* ROW 4: WELLBEING + THIS IS FOR YOU IF */}
        <div className="tt-row">
          <label
            className={`tt-diagnostic-card ${formData.diagnosticType === 'psychological_flexibility' ? 'selected' : ''}`}
            onClick={() => handleDiagnosticSelect('psychological_flexibility')}
          >
            <input
              type="radio"
              name="diagnosticType"
              value="psychological_flexibility"
              checked={formData.diagnosticType === 'psychological_flexibility'}
              onChange={() => handleDiagnosticSelect('psychological_flexibility')}
              className="tt-diagnostic-radio"
            />
            <span className="tt-checkbox-indicator"></span>
            <div className="tt-diagnostic-icon">
              <img src={diagnosticOptions[2].icon} alt={`${diagnosticOptions[2].title} icon`} />
            </div>
            <div className="tt-diagnostic-content">
              <h3 className="tt-diagnostic-title">{diagnosticOptions[2].title}</h3>
              <p className="tt-diagnostic-description">
                {diagnosticOptions[2].description}
                <br/><br/>
                <strong>{diagnosticOptions[2].cta}</strong>
              </p>
              <div className="tt-selected-indicator">
                <span className="tt-selected-check">✓</span> Diagnostic selected
              </div>
            </div>
          </label>

          <div className="tt-info-card">
            <div className="tt-diagnostic-icon">
              <img src="/images/truth-tellers/list.svg" alt="Checklist icon" />
            </div>
            <div className="tt-diagnostic-content">
              <h3 className="tt-info-title">THIS IS FOR YOU IF...</h3>
              <p className="tt-info-description">
                You're accountable yet delivery is problematic.<br/>
                Your dashboards show green while people burn out.<br/>
                You're holding it together and wonder:<br/><br/>
                <em>Could this stand without me?</em>
              </p>
            </div>
          </div>
        </div>

        {/* ROW 5: FORM */}
        <section className="tt-form-row">
          <div className="tt-form-container">
            <h3 className="tt-form-title">Think you can handle the Truth?</h3>

            <form onSubmit={handleSubmit}>
              <div className="tt-form-group">
                <label htmlFor="firstName" className="tt-form-label">First name*</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className={`tt-form-input ${errors.firstName ? 'error' : ''}`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && <p className="tt-field-error">{errors.firstName}</p>}
              </div>

              <div className="tt-form-group">
                <label htmlFor="lastName" className="tt-form-label">Last name*</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className={`tt-form-input ${errors.lastName ? 'error' : ''}`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && <p className="tt-field-error">{errors.lastName}</p>}
              </div>

              <div className="tt-form-group">
                <label htmlFor="email" className="tt-form-label">Email*</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleEmailBlur}
                  className={`tt-form-input ${errors.email ? 'error' : ''}`}
                  placeholder="you@company.com"
                />
                {errors.email && <p className="tt-field-error">{errors.email}</p>}
              </div>

              <div className="tt-gdpr-container">
                <label className="tt-gdpr-label">
                  <input
                    type="checkbox"
                    id="gdprConsent"
                    name="gdprConsent"
                    checked={formData.gdprConsent}
                    onChange={handleInputChange}
                    className="tt-gdpr-checkbox"
                  />
                  <span className="tt-gdpr-text">
                    I agree to allow Daring Futures to store my data for their use only, per{' '}
                    <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Privacy+and+Cookies" target="_blank" rel="noopener noreferrer">Data Protection Policy</a> and{' '}
                    <a href="https://wiki.daringfutures.com/Working+With+Us/General+Policies/Policy+-+Privacy+and+Cookies" target="_blank" rel="noopener noreferrer">GDPR</a> policy for more information.
                  </span>
                </label>
                {errors.gdprConsent && <p className="tt-field-error">{errors.gdprConsent}</p>}
              </div>

              {errors.general && (
                <div className="tt-error-message">{errors.general}</div>
              )}

              {errors.diagnosticType && (
                <div className="tt-error-message">{errors.diagnosticType}</div>
              )}

              {submitMessage && (
                <div className="tt-success-message">{submitMessage}</div>
              )}

              <button type="submit" className="tt-submit-btn" disabled={isSubmitting}>
                <span>{isSubmitting ? 'Sending...' : 'I dare to'}</span>
                {!isSubmitting && (
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                )}
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer minimal />
    </div>
  );
}

export default TruthTellersRequest;
